/**
 * @fileoverview Custom React hook for managing trip documents
 *
 * This module provides the useTripDocuments hook which handles:
 * - Document fetching and state management for active and deleted documents
 * - Presigned download URL generation and mapping
 * - Recently uploaded document tracking for visual feedback
 * - Document deletion with user confirmation
 * - Automatic cleanup and refresh on modal reopen
 *
 * @module useTripDocuments
 */

import { createNotification, queryInput } from '@/utils';
import { useEffect, useState } from 'react';

import Toast from '@/models/Toast';
import { deleteTripDocumentBulk } from '@/features/Trips/api/services/deleteTripDocumentBulk';
import { recreateTripDocumentDownloadUrl } from '@/features/Trips/api/services/createTripDocumentDownloadUrl';
import useConfirmation from '@/hooks/useConfirmation';
import { useTripDocument } from '@/features/Trips/api/services/searchTripDocument';

/**
 * Options for the useTripDocuments hook
 */
export interface UseTripDocumentsOptions {
  /** The unique identifier of the trip */
  tripId: string | undefined;
  /** The mode of the trip modal - 'editing' for existing trips, 'creating' for new trips */
  mode: 'editing' | 'creating';
}

/**
 * Return type for the useTripDocuments hook
 */
export interface UseTripDocumentsReturn {
  /** Array of active (non-deleted) trip documents with download URLs */
  documents: any[];
  /** Array of deleted trip documents with download URLs */
  deletedDocuments: any[];
  /** Set of document IDs that were uploaded in the current session and should show as "Complete" */
  recentlyUploadedIds: Set<string>;
  /** Loading state indicating if document URLs are being fetched */
  isLoading: boolean;
  /** Function to delete a document by ID with user confirmation */
  handleDeleteDocument: (documentId: string) => Promise<void>;
  /**
   * Function to handle successful document upload
   * @param tripId - The trip ID for fetching updated documents
   * @returns Array of document metadata with MD5 hashes and URLs
   */
  handleUploadSuccess: (tripId: string) => Promise<any[]>;
  /** Function to manually refetch trip documents */
  refetchDocuments: () => void;
}

/**
 * Custom React hook for managing trip documents
 *
 * This hook provides comprehensive document management functionality including:
 * - Fetching active and deleted documents
 * - Generating and mapping presigned download URLs
 * - Tracking recently uploaded documents for UI feedback
 * - Handling document deletion with confirmation
 * - Managing document state across modal open/close
 *
 * @param options - Configuration options for the hook
 * @returns Document data and management functions
 *
 * @example
 * ```tsx
 * const {
 *   documents,
 *   deletedDocuments,
 *   recentlyUploadedIds,
 *   handleDeleteDocument,
 *   handleUploadSuccess
 * } = useTripDocuments({ tripId: trip?.id, mode: 'editing' });
 * ```
 */
export const useTripDocuments = ({ tripId, mode }: UseTripDocumentsOptions): UseTripDocumentsReturn => {
  const [{ data: documentsData }, { refetch: fetchDocuments }] = useTripDocument();
  const [documents, setDocuments] = useState(documentsData?.documents || []);
  const [deletedDocuments, setDeletedDocuments] = useState(documentsData?.deletedDocuments || []);
  const [recentlyUploadedIds, setRecentlyUploadedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const confirmDeleteDocument = useConfirmation();

  /**
   * Effect: Fetch documents and reset recently uploaded tracking when tripId changes
   * This ensures that when the modal is reopened for a different trip or the same trip,
   * the "Complete" status indicators are cleared and documents are refetched
   */
  useEffect(() => {
    if (tripId && mode === 'editing') {
      fetchDocuments([{ tripId: queryInput(tripId) }]);
      // Clear recently uploaded IDs when reopening the modal
      setRecentlyUploadedIds(new Set());
    }
  }, [tripId, mode, fetchDocuments]);

  /**
   * Effect: Map presigned download URLs to documents and deleted documents
   *
   * When document data changes, this effect:
   * 1. Fetches presigned download URLs for the trip
   * 2. Matches URLs to documents by MD5 hash
   * 3. Ensures document uniqueness by ID
   * 4. Updates both active and deleted document states
   */
  useEffect(() => {
    const fetchDownloadUrls = async () => {
      // Skip processing if no tripId or no documents to process
      if (!tripId || (!documentsData?.documents?.length && !documentsData?.deletedDocuments?.length)) {
        setDocuments([]);
        setDeletedDocuments([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Fetch presigned download URLs for all documents in this trip
        const downloadUrlsResponse = await recreateTripDocumentDownloadUrl({ tripId: [tripId] });

        // Match and map download URLs to active documents by MD5 hash
        const documentsWithUrls = (documentsData?.documents || []).map((document) => {
          const matchingUrl = downloadUrlsResponse?.urls?.find((urlData) => {
            const md5Match = urlData.url.match(/trip_document\/([a-f0-9]{32})\//);
            const md5 = md5Match ? md5Match[1] : null;
            return md5 === document.md5;
          });

          return {
            ...document,
            fileLocation: matchingUrl?.url || document.fileLocation,
          };
        });

        // Match and map download URLs to deleted documents by MD5 hash
        const deletedDocumentsWithUrls = (documentsData?.deletedDocuments || []).map((document) => {
          const matchingUrl = downloadUrlsResponse?.urls?.find((urlData) => {
            const md5Match = urlData.url.match(/trip_document\/([a-f0-9]{32})\//);
            const md5 = md5Match ? md5Match[1] : null;
            return md5 === document.md5;
          });

          return {
            ...document,
            fileLocation: matchingUrl?.url || document.fileLocation,
          };
        });

        // De-duplicate documents by ID to prevent duplicate entries in the UI
        // Uses Map to maintain insertion order while ensuring uniqueness
        const uniqueDocuments = Array.from(new Map(documentsWithUrls.map((doc) => [doc.id, doc])).values());
        const uniqueDeletedDocuments = Array.from(new Map(deletedDocumentsWithUrls.map((doc) => [doc.id, doc])).values());

        setDocuments(uniqueDocuments);
        setDeletedDocuments(uniqueDeletedDocuments);
      } catch (error) {
        console.error('Error fetching download URLs', error);
        // Fallback: use documents without download URLs on error
        setDocuments(documentsData?.documents || []);
        setDeletedDocuments(documentsData?.deletedDocuments || []);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDownloadUrls();
  }, [documentsData, tripId]);

  /**
   * Handles document deletion with user confirmation
   *
   * Workflow:
   * 1. Prompts user for confirmation
   * 2. Calls delete API if confirmed
   * 3. Refetches documents (deleted document moves to deletedDocuments array)
   * 4. Removes document ID from recently uploaded tracking
   * 5. Shows success/error notification
   *
   * @param documentId - The unique identifier of the document to delete
   */
  const handleDeleteDocument = async (documentId: string): Promise<void> => {
    const confirmed = await confirmDeleteDocument();
    if (!confirmed) return;

    try {
      await deleteTripDocumentBulk([{ id: documentId }]);
      createNotification('Successfully Deleted Document', Toast.Type.SUCCESS, 'Trip Document');
      // Refetch documents to update both active and deleted lists
      // The deleted document will now appear in deletedDocuments array
      if (tripId) {
        await fetchDocuments([{ tripId: queryInput(tripId) }]);
      }
      // Remove the document from recently uploaded tracking
      setRecentlyUploadedIds((current) => {
        const updated = new Set(current);
        updated.delete(documentId);
        return updated;
      });
    } catch (e) {
      console.error('Error deleting document', e);
      createNotification('Could Not Delete Document', Toast.Type.DANGER, 'Trip Document');
    }
  };

  /**
   * Handles successful document upload
   *
   * This function is called after files are successfully uploaded to track which
   * documents should display with "Complete" status and 100% progress.
   *
   * Workflow:
   * 1. Captures current document IDs before refetch
   * 2. Refetches documents to get newly uploaded ones
   * 3. Generates presigned download URLs
   * 4. Identifies new documents by comparing IDs
   * 5. Adds new document IDs to recentlyUploadedIds (accumulative - doesn't replace)
   *
   * Note: Recently uploaded status persists across multiple uploads in the same session
   * and is only cleared when the modal is reopened (tripId changes)
   *
   * @param tripId - The trip ID for fetching updated documents
   * @returns Array of document metadata with MD5 hashes and URLs for the upload component
   */
  const handleUploadSuccess = async (tripId: string): Promise<any[]> => {
    try {
      const previousDocumentIds = new Set(documents.map((doc) => doc.id));
      await fetchDocuments([{ tripId: queryInput(tripId) }]);
      const downloadUrlsResponse = await recreateTripDocumentDownloadUrl({ tripId: [tripId] });
      const res = downloadUrlsResponse.urls.map((document) => {
        const md5Match = document.url.match(/trip_document\/([a-f0-9]{32})\//);
        const md5 = md5Match ? md5Match[1] : null;
        return {
          md5,
          url: document.url,
        };
      });

      // Identify and track newly uploaded documents after a brief delay
      // The timeout ensures the documents state has been updated by the effect
      setTimeout(() => {
        setDocuments((current) => {
          const newlyUploadedIds = current.filter((doc) => !previousDocumentIds.has(doc.id)).map((doc) => doc.id);
          if (newlyUploadedIds.length > 0) {
            // Add new IDs to existing set (accumulative) so all uploads in this session show as "Complete"
            setRecentlyUploadedIds((prevIds) => {
              const updated = new Set(prevIds);
              newlyUploadedIds.forEach((id) => updated.add(id));
              return updated;
            });
          }
          return current;
        });
      }, 100);

      return res;
    } catch (e) {
      console.error('Error fetching download URLs after upload', e);
      return [];
    }
  };

  /**
   * Manually refetch trip documents
   *
   * Useful for forcing a document list refresh without changing the tripId.
   * This will trigger the download URL mapping effect.
   */
  const refetchDocuments = () => {
    if (tripId) {
      fetchDocuments([{ tripId: queryInput(tripId) }]);
    }
  };

  return {
    documents,
    deletedDocuments,
    recentlyUploadedIds,
    isLoading,
    handleDeleteDocument,
    handleUploadSuccess,
    refetchDocuments,
  };
};
